import {
  differenceInCalendarDays,
  formatRelative as formatRelativeFns,
  type Locale,
} from "date-fns";
import { enGB, es, fr } from "date-fns/locale";

/**
 * Locale-aware date/time formatting shared by the whole app.
 *
 * Every helper takes the app locale (`es` | `en` | `fr`, as stored by
 * react-admin's `useLocaleState`) and always renders times on a 24-hour clock
 * without seconds. Components should use the `useDateFormat` hook
 * (`@/hooks/use-date-format`), which binds these helpers to the current locale.
 */

/** Value accepted by the helpers: a Date, an ISO string, a timestamp. */
export type DateValue = Date | string | number | null | undefined;

/**
 * App locale -> Intl locale. English uses en-GB so every locale shares the
 * day-month-year order and the 24-hour clock.
 */
const INTL_LOCALES: Record<string, string> = {
  es: "es-ES",
  en: "en-GB",
  fr: "fr-FR",
};

const DATE_FNS_LOCALES: Record<string, Locale> = { es, en: enGB, fr };

const DEFAULT_LOCALE = "es";

const PLAIN_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** "31 oct 2026" */
export const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

/** "01:18" */
export const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

/** "31 oct 2026, 01:18" */
export const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_FORMAT,
  ...TIME_FORMAT,
};

const baseLocale = (locale?: string | null): string =>
  (locale || DEFAULT_LOCALE).toLowerCase().split(/[-_]/)[0];

/** Intl locale tag for an app locale ("es" -> "es-ES"). */
export const toIntlLocale = (locale?: string | null): string =>
  INTL_LOCALES[baseLocale(locale)] ?? locale ?? INTL_LOCALES[DEFAULT_LOCALE];

/** date-fns locale for an app locale (English uses en-GB, 24-hour clock). */
export const toDateFnsLocale = (locale?: string | null): Locale =>
  DATE_FNS_LOCALES[baseLocale(locale)] ?? DATE_FNS_LOCALES[DEFAULT_LOCALE];

/** True for a calendar date without time ("2026-10-31"). */
export const isPlainDate = (value: unknown): value is string =>
  typeof value === "string" && PLAIN_DATE_REGEX.test(value);

/**
 * Parses a date value; returns null when empty or invalid. Plain dates
 * ("2026-10-31") are read as local midnight, not UTC midnight, so they never
 * shift by a day in negative-offset time zones.
 */
export const parseDate = (value: DateValue): Date | null => {
  if (value == null || value === "") return null;
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (isPlainDate(value)) {
    const [year, month, day] = value.split("-").map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(value);
  }
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Forces the 24-hour clock whenever the options show the hour, unless the
 * caller explicitly chose a clock.
 */
const with24h = (
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions =>
  (options.hour !== undefined || options.timeStyle !== undefined) &&
  options.hour12 === undefined &&
  options.hourCycle === undefined
    ? { ...options, hourCycle: "h23" }
    : options;

const format = (
  value: DateValue,
  locale: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string => {
  const date = parseDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(toIntlLocale(locale), with24h(options)).format(
    date,
  );
};

/** "31 oct 2026" (es), "31 Oct 2026" (en), "31 oct. 2026" (fr). "" if invalid. */
export const formatDate = (
  value: DateValue,
  locale?: string | null,
  options: Intl.DateTimeFormatOptions = DATE_FORMAT,
): string => format(value, locale, options);

/**
 * "31 oct 2026, 01:18". A plain date ("2026-10-31") has no time of day, so it
 * is rendered as a date only rather than with a fake "00:00".
 */
export const formatDateTime = (
  value: DateValue,
  locale?: string | null,
  options: Intl.DateTimeFormatOptions = DATE_TIME_FORMAT,
): string =>
  isPlainDate(value)
    ? format(value, locale, DATE_FORMAT)
    : format(value, locale, options);

/** "01:18" (24-hour clock, no seconds). "" if invalid. */
export const formatTime = (value: DateValue, locale?: string | null): string =>
  format(value, locale, TIME_FORMAT);

/**
 * Relative date with time within a week ("hoy a las 13:05", "yesterday at
 * 09:30", "mardi dernier à 18:00"), absolute date beyond ("31 oct 2026").
 */
export const formatRelative = (
  value: DateValue,
  locale?: string | null,
  now: Date = new Date(),
): string => {
  const date = parseDate(value);
  if (!date) return "";
  if (Math.abs(differenceInCalendarDays(now, date)) > 6) {
    return formatDate(date, locale);
  }
  return formatRelativeFns(date, now, { locale: toDateFnsLocale(locale) });
};

/**
 * Relative day without time ("Hoy", "Ayer", "Hace 3 días") within a week,
 * day and month beyond ("31 de octubre").
 */
export const formatRelativeDay = (
  value: DateValue,
  locale?: string | null,
  now: Date = new Date(),
): string => {
  const date = parseDate(value);
  if (!date) return "";
  const dayDiff = differenceInCalendarDays(date, now);
  if (Math.abs(dayDiff) > 7) {
    return formatDate(date, locale, { day: "numeric", month: "long" });
  }
  const relative = new Intl.RelativeTimeFormat(toIntlLocale(locale), {
    numeric: "auto",
  }).format(dayDiff, "day");
  return relative.charAt(0).toUpperCase() + relative.slice(1);
};
